import escapeRegExp from 'lodash/escapeRegExp';

/**
 * Optimizely feature gating the Campaign Type filter (top bar chip + default exclusion).
 */
export const CAMPAIGN_TYPE_FILTER_FEATURE_FLAG = 'techmerc-2008';

// Poorly implemented filter due to customer specificity: campaign types are inferred from the campaign
// naming convention '<prefix>_<TYPE>_...' and only make sense for the projects listed here.
export const CAMPAIGN_TYPE_FILTER_ENABLED_PROJECT_IDS = [
  '24df4736-5c84-456f-bfa2-59f230f22f23', // Call of Duty Mobile
];

const UA_TEST_CAMPAIGN_TYPE = 'UA-TEST';
const REPLAI_CAMPAIGN_MARKER = 'Replai';

/**
 * UA-TEST campaigns whose name also carries the Replai marker are a campaign type of their own, so that
 * they can be included/excluded independently of the remaining UA-TEST campaigns.
 */
export const UA_TEST_REPLAI_CAMPAIGN_TYPE = `${UA_TEST_CAMPAIGN_TYPE} ${REPLAI_CAMPAIGN_MARKER}`;

/** Campaign types excluded by default when the filter is enabled and the project has campaigns of that type. */
export const DEFAULT_EXCLUDED_CAMPAIGN_TYPES = [UA_TEST_REPLAI_CAMPAIGN_TYPE];

const ALLOWED_CAMPAIGN_TYPES: Record<string, string[]> = {
  '24df4736-5c84-456f-bfa2-59f230f22f23': [
    'UA-TROAS',
    'UA-PURCHASE',
    UA_TEST_CAMPAIGN_TYPE,
    UA_TEST_REPLAI_CAMPAIGN_TYPE,
    'UA-RETENTION',
    'UA- RETENTION',
    'RT',
    'UA-LEVEL',
    'UA-INSTALL',
    'BRAND',
    'UA-REGISTRATION',
    'UA-TUTORIAL',
  ],
};

const hasReplaiMarker = (campaignName: string) => new RegExp(REPLAI_CAMPAIGN_MARKER, 'i').test(campaignName);

/**
 * Campaign type of a campaign given its name: the second '_' separated segment. UA-TEST campaigns are split
 * between the plain UA-TEST type and the UA-TEST Replai type depending on the Replai marker.
 */
export function getCampaignType(campaignName: string): string | undefined {
  const baseType = campaignName.split('_')[1];

  if (baseType == null) {
    return undefined;
  }

  if (baseType === UA_TEST_CAMPAIGN_TYPE && hasReplaiMarker(campaignName)) {
    return UA_TEST_REPLAI_CAMPAIGN_TYPE;
  }

  return baseType;
}

export function isCampaignTypeAllowed(projectId: string, campaignType: string): boolean {
  return (ALLOWED_CAMPAIGN_TYPES[projectId] ?? []).includes(campaignType);
}

/**
 * Postgres (ARE) regular expression, matched case-insensitively by the backend against the campaign name,
 * that selects exactly the campaigns of the given type - see `getCampaignType`. Types are disjoint, so
 * including/excluding one never affects another.
 */
export function getCampaignTypePattern(campaignType: string): string {
  // '<anything but underscore>_<TYPE>' followed by another segment or the end of the name
  const secondSegmentIs = (type: string) => `[^_]*_${escapeRegExp(type)}(_|$)`;

  if (campaignType === UA_TEST_REPLAI_CAMPAIGN_TYPE) {
    return `^(?=.*${REPLAI_CAMPAIGN_MARKER})${secondSegmentIs(UA_TEST_CAMPAIGN_TYPE)}`;
  }

  if (campaignType === UA_TEST_CAMPAIGN_TYPE) {
    return `^(?!.*${REPLAI_CAMPAIGN_MARKER})${secondSegmentIs(UA_TEST_CAMPAIGN_TYPE)}`;
  }

  return `^${secondSegmentIs(campaignType)}`;
}
